import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw, X, Image as ImageIcon, CheckCircle } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const DEFAULT_STATUS_OPTIONS = [
  { Name: 'Payment Done' },
  { Name: 'Payment Return' },
];

const isProcessingStatus = (status) =>
  String(status || '').trim().toLowerCase() === 'processing';

const PaymentStatus = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('Payment Done');
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const profile = await MainService.GetCompanyProfile();
        if (profile?.BlockPayRetn) {
          setStatusOptions([{ Name: 'Payment Done' }]);
        }
      } catch (err) {
        console.error('Error loading company profile:', err);
      }
    };
    loadCompanyProfile();
  }, []);

  const loadClients = async () => {
    if (!paymentId) return;
    try {
      setLoading(true);
      setError('');

      let allData = [];
      let skip = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await MainService.CPClientsInStatusList({
          ClientPaymentId: Number(paymentId),
          Skip: skip,
          Take: 200,
        });

        if (response?.Data?.length > 0) {
          allData = [...allData, ...response.Data.map((row) => ({ ...row, IsSelected: false }))];
          skip += response.Data.length;
          if (allData.length >= response.Total || response.Data.length === 0) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setClients(allData);
      setSelectAll(false);
    } catch (err) {
      console.error('Error loading payment clients:', err);
      setError('Failed to load payment clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [paymentId]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setClients((prev) => prev.map((row) => ({ ...row, IsSelected: newSelectAll })));
  };

  const handleToggleRow = (rowId) => {
    setClients((prev) => {
      const updated = prev.map((row) =>
        row.Id === rowId ? { ...row, IsSelected: !row.IsSelected } : row
      );
      setSelectAll(updated.length > 0 && updated.every((row) => row.IsSelected));
      return updated;
    });
  };

  const handleApplyBulkStatus = () => {
    if (!bulkStatus) {
      setError('Select a status');
      return;
    }
    const hasSelected = clients.some((row) => row.IsSelected);
    if (!hasSelected) {
      setError('Select at least one client row to apply status');
      return;
    }
    setClients((prev) =>
      prev.map((row) =>
        row.IsSelected ? { ...row, Status: bulkStatus, IsSelected: false } : row
      )
    );
    setSelectAll(false);
  };

  const handleRowChange = (rowId, field, value) => {
    setClients((prev) =>
      prev.map((row) => (row.Id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const handleViewOrders = async (rowId) => {
    try {
      setOrdersLoading(true);
      const response = await MainService.getCPOrdersByCPCId(rowId);
      setSelectedOrders(Array.isArray(response) ? response : response?.Data || []);
      setShowOrdersModal(true);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    let clientsToSave = clients;

    if (bulkStatus) {
      clientsToSave = clients.map((row) =>
        isProcessingStatus(row.Status) ? { ...row, Status: bulkStatus } : row
      );
      setClients(clientsToSave);
    }

    const processingCount = clientsToSave.filter((row) => isProcessingStatus(row.Status)).length;
    if (processingCount > 0) {
      setError(
        'Update status for all clients before saving. Select Payment Done or Payment Return from the dropdown, or change each row status, then click Save Status.'
      );
      return;
    }

    try {
      setLoading(true);
      const response = await MainService.updateClientPaymentStatus({
        Id: Number(paymentId),
        ClientPaymentClients: clientsToSave,
      });

      if (response === 'Success') {
        setSuccess('Status saved successfully');
        navigate('/home/payments/pending-payments');
      } else {
        setError(response || 'Failed to save status');
      }
    } catch (err) {
      console.error('Error saving payment status:', err);
      setError('Failed to save status');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const resolveImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const base = getApiBaseUrl();
    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return `${base}${normalized}`;
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-24 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <X className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-24 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-[100] min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getPageHeading(language, 'clientPaymentStatus')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">Update client payment status for payment #{paymentId}</p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent min-w-[180px]"
              >
                <option value="">Select status</option>
                {statusOptions.map((option) => (
                  <option key={option.Name} value={option.Name}>{option.Name}</option>
                ))}
              </select>
              <button
                onClick={handleApplyBulkStatus}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Apply to Selected
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadClients}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 inline-block ${loading ? 'animate-spin' : ''}`} />
                <span className="ml-2">Refresh</span>
              </button>
              <button
                onClick={handleSaveStatus}
                disabled={loading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                Save Status
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="w-4 h-4" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Ref</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client No</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Client Money</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Signature</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Proof</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Options</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && clients.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Loading clients...</p>
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-12 text-center text-gray-500">No clients found</td>
                </tr>
              ) : (
                clients.map((row) => (
                  <tr key={row.Id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={row.IsSelected || false}
                        onChange={() => handleToggleRow(row.Id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.RefNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.ClientPayment?.RefNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.Client?.Name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.Client?.MobileNo1 || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.Client?.ClientNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">AED {formatCurrency(row.ClientMoney)}</td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={isProcessingStatus(row.Status) ? '' : (row.Status || statusOptions[0]?.Name || '')}
                        onChange={(e) => handleRowChange(row.Id, 'Status', e.target.value)}
                        className={`px-2 py-1 border border-gray-300 rounded-lg text-sm min-w-[140px] ${
                          row.Status === 'Payment Done' ? 'text-green-700 font-medium' : ''
                        }`}
                      >
                        {isProcessingStatus(row.Status) && (
                          <option value="">Processing</option>
                        )}
                        {statusOptions.map((option) => (
                          <option key={option.Name} value={option.Name}>{option.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={row.PayMode || 'CASH'}
                        onChange={(e) => handleRowChange(row.Id, 'PayMode', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="CASH">CASH</option>
                        <option value="BANK">BANK</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <textarea
                        rows={1}
                        value={row.DriverRemarks || ''}
                        onChange={(e) => handleRowChange(row.Id, 'DriverRemarks', e.target.value)}
                        className="w-full min-w-[120px] px-2 py-1 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setImagePreview(resolveImageUrl(row.Signature))}
                        disabled={!row.Signature}
                        className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-40"
                        title="View signature"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setImagePreview(resolveImageUrl(row.Proof))}
                        disabled={!row.Proof}
                        className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-40"
                        title="View proof"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewOrders(row.Id)}
                        disabled={ordersLoading}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showOrdersModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Orders</h3>
              <button onClick={() => { setShowOrdersModal(false); setSelectedOrders([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">#</th>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">Voucher No</th>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">Delivery Date</th>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">Customer</th>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">Mobile</th>
                    <th className="px-3 py-2 text-right text-xs border border-gray-300">Product</th>
                    <th className="px-3 py-2 text-right text-xs border border-gray-300">Service</th>
                    <th className="px-3 py-2 text-right text-xs border border-gray-300">VAT</th>
                    <th className="px-3 py-2 text-right text-xs border border-gray-300">Client Money</th>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">Pay Type</th>
                    <th className="px-3 py-2 text-center text-xs border border-gray-300">Advance Charge</th>
                    <th className="px-3 py-2 text-center text-xs border border-gray-300">Advance Price</th>
                    <th className="px-3 py-2 text-center text-xs border border-gray-300">Free</th>
                    <th className="px-3 py-2 text-left text-xs border border-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrders.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="px-4 py-4 text-center text-gray-500">No orders found</td>
                    </tr>
                  ) : (
                    selectedOrders.map((row, index) => {
                      const order = row.Order || {};
                      return (
                        <tr key={row.Id || index}>
                          <td className="px-3 py-2 border border-gray-300">{index + 1}</td>
                          <td className="px-3 py-2 border border-gray-300">{order.VoucherNo}</td>
                          <td className="px-3 py-2 border border-gray-300">{formatDate(order.DeliveryDate)}</td>
                          <td className="px-3 py-2 border border-gray-300">{order.Customer?.Name}</td>
                          <td className="px-3 py-2 border border-gray-300">{order.Customer?.MobileNo1}</td>
                          <td className="px-3 py-2 border border-gray-300 text-right">AED {formatCurrency(order.ProductPrice)}</td>
                          <td className="px-3 py-2 border border-gray-300 text-right">AED {formatCurrency(order.DeliveryCharge)}</td>
                          <td className="px-3 py-2 border border-gray-300 text-right">AED {formatCurrency(order.TAX)}</td>
                          <td className="px-3 py-2 border border-gray-300 text-right">AED {formatCurrency(order.ClientAmount)}</td>
                          <td className="px-3 py-2 border border-gray-300">{order.PayType}</td>
                          <td className="px-3 py-2 border border-gray-300 text-center">{order.IsAdvancePay ? 'Yes' : 'No'}</td>
                          <td className="px-3 py-2 border border-gray-300 text-center">{order.IsAdvancePrice ? 'Yes' : 'No'}</td>
                          <td className="px-3 py-2 border border-gray-300 text-center">{order.IsFree ? 'Yes' : 'No'}</td>
                          <td className="px-3 py-2 border border-gray-300">{order.Status?.Name}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => { setShowOrdersModal(false); setSelectedOrders([]); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {imagePreview && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-75 flex items-center justify-center z-[99999] p-4">
          <div className="relative max-w-3xl w-full">
            <button
              onClick={() => setImagePreview(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
            <img src={imagePreview} alt="Preview" className="max-h-[80vh] mx-auto rounded-lg bg-white" />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PaymentStatus;
